import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useSegmentDetail } from "../api/useSegmentDetail";
import { useSegments } from "../api/useSegments";

import { AsyncSelect } from "@/components/ui/v2/async-select";
import { FormLabel } from "@/components/ui/v2/form";
import { Label } from "@/components/ui/v2/label";
import type { GetSegmentsResponse } from "@/types/response/GetSegmentsResponse";

export type SegmentOption = {
  value: string;
  label: string;
};

interface SegmentSelectProps {
  fieldName?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  clientId: string | undefined;
  excludeSegmentIds?: string[];
  useFormLabel?: boolean;
  /** Whether the field has an error (for destructive styling) */
  "aria-invalid"?: boolean;
}

export function SegmentSelect({
  fieldName = "segmentId",
  label,
  placeholder = "Select a segment",
  disabled,
  clientId,
  excludeSegmentIds,
  useFormLabel = false,
  "aria-invalid": ariaInvalid,
}: SegmentSelectProps) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const [searchTerm, setSearchTerm] = useState("");

  const { data } = useSegments({
    clientId,
    search: searchTerm,
  });

  const selectedSegmentId = useWatch({ control, name: fieldName }) as
    string | undefined;

  const { data: selectedSegment } = useSegmentDetail({
    segmentId: selectedSegmentId,
    clientId,
  });
  const isSelectedArchived = selectedSegment?.archivedAt != null;

  const transformData = (response: GetSegmentsResponse): SegmentOption[] => {
    if (!response || !response.data) {
      return [];
    }

    const allOptions = response.data.map((segment) => ({
      label: segment.name,
      value: segment.id,
    }));

    // Filter out excluded segment IDs, but keep the currently selected segment visible
    const filteredOptions = allOptions.filter(
      (opt) =>
        !excludeSegmentIds?.includes(opt.value) ||
        opt.value === selectedSegmentId,
    );

    if (!selectedSegmentId || !selectedSegment) {
      return filteredOptions;
    }

    const isAlreadyInOptions = filteredOptions.some(
      (opt) => opt.value === selectedSegmentId,
    );

    if (isAlreadyInOptions) {
      return filteredOptions;
    }

    return [
      {
        value: selectedSegment.id,
        label: selectedSegment.name,
      },
      ...filteredOptions,
    ];
  };

  const fetcher = async (query?: string) => {
    setSearchTerm(query || "");

    // Wait for React Query to update with new search term
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Return transformed data if available
    if (data) {
      return transformData(data);
    }

    return [];
  };

  const filterFn = (option: SegmentOption, query: string) => {
    return option.label.toLowerCase().includes(query.toLowerCase());
  };

  const LabelComponent = useFormLabel ? FormLabel : Label;

  return (
    <div className="space-y-2">
      {label && <LabelComponent>{label}</LabelComponent>}
      <AsyncSelect
        width={"100%"}
        fetcher={fetcher}
        filterFn={filterFn}
        value={selectedSegmentId || ""}
        onChange={(value) => {
          setValue(fieldName, value, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        }}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        renderOption={(option: SegmentOption) => <span>{option.label}</span>}
        getOptionValue={(option: SegmentOption) => option.value}
        getDisplayValue={(option: SegmentOption) => option.label}
        noResultsMessage={t("commonSentences.noSegmentsFound")}
      />
      {isSelectedArchived && (
        <p
          className="flex items-center gap-1.5 text-sm text-warning"
          data-testid="segment-select-archived"
        >
          <TriangleAlert className="size-4 shrink-0" aria-hidden />
          {t("admin.segments.archive.selectedArchived")}
        </p>
      )}
    </div>
  );
}
